import React, { useEffect, useRef, useState } from "react";

import { Country, State } from "country-state-city";
import Select from "react-select";

import PaymentSection from "./gateways/PaymentSection";
import { isVendorSupported } from "./gateways";

import SanitizedHTML from "../../../HTML/SanitizedHTML";

// ============================================================
// HELPERS
// ============================================================

const COMMON_EMAIL_PROVIDERS = [
  "gmail",
  "google",
  "hotmail",
  "microsoft",
  "outlook",
  "live",
  "yahoo",
  "icloud",
  "qq",
  "163",
  "naver",
  "daum",
  "aol",
  "proton",
  "gmx",
];

const MAX_CONTACT_LEN = 25;

const getIso2 = (country) => {
  return (country?.iso2 || country?.value || "").toString().toUpperCase();
};

const digitsOnly = (value) => {
  return String(value || "").replace(/\D/g, "");
};

const normalizeEmail = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase();
};

// ============================================================
// LEVENSHTEIN
// ============================================================

const levenshtein = (a, b) => {
  const matrix = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      matrix[i][j] =
        b[i - 1] === a[j - 1]
          ? matrix[i - 1][j - 1]
          : 1 +
            Math.min(matrix[i - 1][j], matrix[i][j - 1], matrix[i - 1][j - 1]);
    }
  }

  return matrix[b.length][a.length];
};

const splitEmail = (value) => {
  const normalizedValue = normalizeEmail(value);

  const at = normalizedValue.lastIndexOf("@");

  if (at <= 0 || at === normalizedValue.length - 1) {
    return null;
  }

  const local = normalizedValue.slice(0, at);
  const domain = normalizedValue.slice(at + 1);

  if (!local || !domain || !domain.includes(".")) {
    return null;
  }

  return {
    local,
    domain,
  };
};

const extractDomainLabelAndSuffix = (domain) => {
  const parts = domain.split(".").filter(Boolean);

  if (parts.length < 2) {
    return null;
  }

  const label = parts[parts.length - 2];

  const suffixChain = parts.slice(parts.length - 1).join(".");

  const suffixFull =
    parts.length >= 3 ? parts.slice(parts.length - 2).join(".") : suffixChain;

  return {
    label,
    suffix: suffixFull,
  };
};

const findClosestProvider = (label) => {
  let best = {
    provider: "",
    dist: Infinity,
  };

  for (const provider of COMMON_EMAIL_PROVIDERS) {
    const distance = levenshtein(label, provider);

    if (distance < best.dist) {
      best = {
        provider,
        dist: distance,
      };
    }
  }

  return best;
};

// ============================================================
// REACT SELECT STYLES
// ============================================================

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "44px",
    border: 0,
    boxShadow: "none",
    backgroundColor: "transparent",
  }),

  valueContainer: (base) => ({
    ...base,
    padding: "0 12px",
  }),

  singleValue: (base) => ({
    ...base,
    color: "#212529",
  }),

  placeholder: (base) => ({
    ...base,
    color: "#8a8f98",
  }),

  indicatorSeparator: () => ({
    display: "none",
  }),

  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),

  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused
      ? "#f5f5f5"
      : state.isSelected
        ? "#eaeaea"
        : "#ffffff",
    color: "#212529",
  }),
};

// ============================================================
// PAYMENT FORM
// ============================================================

const PaymentForm = ({
  cta,
  pricePlan,
  store_slug,
  price_slug,
  geolocation = null,
  onLocationChange,
  isRecalculating = false,
  recalculationError = "",

  couponCode = "",
  isFreeTrial = false,
  freeTrialNoCard = false,

  // Everflow
  ef_transaction_id = "",
  ef_oid = "",
  ef_affid = "",
}) => {
  // ============================================================
  // GEOLOCATION
  // ============================================================

  const geoAppliedRef = useRef(false);

  const userTouchedLocationRef = useRef(false);

  const legalContentsHtml = pricePlan?.legal_contents_html || "";

  // ============================================================
  // CTA BUTTON
  // ============================================================

  const ctaLabel = isFreeTrial
    ? cta?.labels?.free_trial || "Start Free Trial →"
    : cta?.labels?.standard || "Complete Enrollment →";

  const ctaBackgroundColor = cta?.background_color || "#1b2a4a";

  const ctaColor = cta?.color || "#ffffff";

  const ctaFontSize = cta?.font_size || 16;

  // ============================================================
  // FIRST NAME
  // ============================================================

  const [firstName, setFirstName] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [firstNameTouched, setFirstNameTouched] = useState(false);

  // ============================================================
  // SURNAME
  // ============================================================

  const [surname, setSurname] = useState("");
  const [surnameError, setSurnameError] = useState("");
  const [surnameTouched, setSurnameTouched] = useState(false);

  // ============================================================
  // EMAIL
  // ============================================================

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);

  // ============================================================
  // COUNTRY
  // ============================================================

  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // ============================================================
  // STATE
  // ============================================================

  const [stateOptions, setStateOptions] = useState([]);
  const [selectedState, setSelectedState] = useState(null);

  const [stateDisabled, setStateDisabled] = useState(true);

  const [stateHint, setStateHint] = useState(
    "Please select country / region first",
  );

  // ============================================================
  // POSTAL CODE
  // ============================================================

  const [postalCode, setPostalCode] = useState("");
  const [postalTouched, setPostalTouched] = useState(false);
  const [postalError, setPostalError] = useState("");

  const [postalDisabled, setPostalDisabled] = useState(true);

  const [postalHint, setPostalHint] = useState(
    "Please select country / region first",
  );

  // ============================================================
  // CONTACT NUMBER
  // ============================================================

  const [dialingCode, setDialingCode] = useState("");
  const [contactNumber, setContactNumber] = useState("");

  const [contactTouched, setContactTouched] = useState(false);
  const [contactError, setContactError] = useState("");

  const contactDisabled = !getIso2(selectedCountry);

  // ============================================================
  // AGREEMENT
  // ============================================================

  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementTouched, setAgreementTouched] = useState(false);

  // ============================================================
  // PAYMENT SUBMISSION
  // ============================================================

  const paymentHandlerRef = useRef(null);
  const processingRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successCountdown, setSuccessCountdown] = useState(null);
  const [paymentErrors, setPaymentErrors] = useState([]);

  // ============================================================
  // VALIDATION
  // ============================================================

  const validateFirstName = (value) => {
    const normalizedValue = String(value || "").trim();

    if (normalizedValue.length < 1) {
      return "🚨 First name is required.";
    }

    return "";
  };

  const validateSurname = (value) => {
    const normalizedValue = String(value || "").trim();

    if (normalizedValue.length < 1) {
      return "🚨 Surname is required.";
    }

    return "";
  };

  const validateEmail = (value) => {
    const normalizedValue = normalizeEmail(value);

    if (!normalizedValue) {
      return "🚨 Email is required.";
    }

    const parsed = splitEmail(normalizedValue);

    if (!parsed) {
      return "🚨 Please enter a valid email address.";
    }

    const { local, domain } = parsed;

    if (domain.endsWith(".con")) {
      return "🚨 Your email does not look right. Do you mean .com?";
    }

    const meta = extractDomainLabelAndSuffix(domain);

    if (!meta) {
      return "";
    }

    const { label, suffix } = meta;

    if (COMMON_EMAIL_PROVIDERS.includes(label)) {
      return "";
    }

    const { provider, dist } = findClosestProvider(label);

    if (dist <= 2) {
      return `😮 This email domain is not commonly seen. It might be ${local}@${provider}.${suffix}?`;
    }

    return "";
  };

  const validatePostal = (value, country) => {
    const iso2 = getIso2(country);

    if (!iso2) {
      return "";
    }

    if (iso2 !== "US" && iso2 !== "CA") {
      return "";
    }

    const rawValue = String(value || "").trim();

    if (!rawValue) {
      return "🚨 ZIP / Postal Code is required.";
    }

    const normalizedValue = rawValue.replace(/\s+/g, "");

    if (normalizedValue.length < 3) {
      return "🚨 ZIP / Postal Code must be at least 3 characters.";
    }

    return "";
  };

  const validateContactNumber = (value, country) => {
    const normalizedValue = String(value || "").trim();

    const iso2 = getIso2(country);

    if (!iso2) {
      return "";
    }

    if (!normalizedValue) {
      return "🚨 Contact number is required.";
    }

    if (normalizedValue.length < 7) {
      return "🚨 Contact number is too short. Must have at least 7 digits.";
    }

    return "";
  };

  // ============================================================
  // VALIDATE CHECKOUT
  // ============================================================

  // Notes for free trial no card condition:
  // - In case need it back to collect billing details for free trial no card in the future
  // - If want to show the billing details field, remember to remove the wrapper below this file
  // - so that the billing details will show
  // - Something like this --> freeTrialNoCard ? "d-none" : ""
  // .
  // const validateCheckout = () => {
  //   const errors = [];

  //   const firstNameErr = validateFirstName(firstName);
  //   const surnameErr = validateSurname(surname);
  //   const emailErr = validateEmail(email);

  //   const postalErr = validatePostal(postalCode, selectedCountry);

  //   const contactErr = validateContactNumber(contactNumber, selectedCountry);

  //   setFirstNameTouched(true);
  //   setSurnameTouched(true);
  //   setEmailTouched(true);
  //   setPostalTouched(true);
  //   setContactTouched(true);
  //   setAgreementTouched(true);

  //   setFirstNameError(firstNameErr);
  //   setSurnameError(surnameErr);
  //   setEmailError(emailErr);
  //   setPostalError(postalErr);
  //   setContactError(contactErr);

  //   if (firstNameErr) errors.push(firstNameErr);
  //   if (surnameErr) errors.push(surnameErr);
  //   if (emailErr) errors.push(emailErr);
  //   if (postalErr) errors.push(postalErr);
  //   if (contactErr) errors.push(contactErr);

  //   const countryIso2 = getIso2(selectedCountry);

  //   if (!countryIso2) {
  //     errors.push("🚨 Country / region is required.");
  //   }

  //   const stateRequired = countryIso2 !== "SG" && stateOptions.length > 0;

  //   if (countryIso2 && stateRequired && !selectedState) {
  //     errors.push("🚨 State / territory is required.");
  //   }

  //   if (!agreementAccepted) {
  //     errors.push(
  //       "🚨 You must agree to the terms before completing your purchase.",
  //     );
  //   }

  //   return errors;
  // };

  // Exclude billing details from validation for free trial no card
  const validateCheckout = () => {
    const errors = [];

    // ============================================================
    // CUSTOMER DETAILS
    // ============================================================

    const firstNameErr = validateFirstName(firstName);
    const surnameErr = validateSurname(surname);
    const emailErr = validateEmail(email);

    setFirstNameTouched(true);
    setSurnameTouched(true);
    setEmailTouched(true);
    setAgreementTouched(true);

    setFirstNameError(firstNameErr);
    setSurnameError(surnameErr);
    setEmailError(emailErr);

    if (firstNameErr) errors.push(firstNameErr);
    if (surnameErr) errors.push(surnameErr);
    if (emailErr) errors.push(emailErr);

    // ============================================================
    // BILLING & CONTACT DETAILS
    // ============================================================

    if (!freeTrialNoCard) {
      const postalErr = validatePostal(postalCode, selectedCountry);

      const contactErr = validateContactNumber(contactNumber, selectedCountry);

      setPostalTouched(true);
      setContactTouched(true);

      setPostalError(postalErr);
      setContactError(contactErr);

      if (postalErr) errors.push(postalErr);
      if (contactErr) errors.push(contactErr);

      const countryIso2 = getIso2(selectedCountry);

      if (!countryIso2) {
        errors.push("🚨 Country / region is required.");
      }

      const stateRequired = countryIso2 !== "SG" && stateOptions.length > 0;

      if (countryIso2 && stateRequired && !selectedState) {
        errors.push("🚨 State / territory is required.");
      }
    } else {
      // Clear validation errors for hidden fields.
      setPostalTouched(false);
      setContactTouched(false);

      setPostalError("");
      setContactError("");
    }

    // ============================================================
    // AGREEMENT
    // ============================================================

    if (!agreementAccepted) {
      errors.push(
        "🚨 You must agree to the terms before completing your purchase.",
      );
    }

    return errors;
  };

  // ============================================================
  // LOAD COUNTRIES
  // ============================================================

  useEffect(() => {
    try {
      const results = Country.getAllCountries();

      const countries = (results || []).map((country, index) => ({
        index,

        value: country.isoCode,

        label: `${country.flag || ""} ${country.name} (${country.isoCode})`,

        name: country.name,

        iso2: country.isoCode,

        phone_code: country.phonecode ? `+${country.phonecode}` : "",

        currency: country.currency || "",

        _raw: country,
      }));

      setCountryOptions(countries);

      // ========================================================
      // AUTO SELECT COUNTRY FROM GEOLOCATION
      // ========================================================

      const geoIso2 = String(geolocation?.country_iso2 || "")
        .trim()
        .toUpperCase();

      if (
        geoIso2 &&
        !geoAppliedRef.current &&
        !userTouchedLocationRef.current
      ) {
        const matchedCountry = countries.find(
          (country) => getIso2(country) === geoIso2,
        );

        if (matchedCountry) {
          setSelectedCountry(matchedCountry);
        }
      }
    } catch (error) {
      console.error("GetCountries failed:", error);

      setCountryOptions([]);
    }
  }, [geolocation]);

  // ============================================================
  // COUNTRY DEPENDENT FIELDS
  // ============================================================

  useEffect(() => {
    // ========================================================
    // RESET STATE
    // ========================================================

    setSelectedState(null);
    setStateOptions([]);

    // ========================================================
    // RESET POSTAL CODE
    // ========================================================

    setPostalCode("");
    setPostalTouched(false);
    setPostalError("");

    // ========================================================
    // RESET CONTACT NUMBER
    // ========================================================

    setContactNumber("");
    setContactTouched(false);
    setContactError("");

    const iso2 = getIso2(selectedCountry);

    // ========================================================
    // CHECK GEOLOCATION
    // ========================================================

    const isGeoMatch =
      !geoAppliedRef.current &&
      !userTouchedLocationRef.current &&
      geolocation &&
      String(geolocation?.country_iso2 || "").toUpperCase() === iso2;

    // ========================================================
    // DIALING CODE
    // ========================================================

    if (!iso2) {
      setDialingCode("");
    } else {
      const code = String(selectedCountry?.phone_code || "").trim();

      setDialingCode(code);
    }

    // ========================================================
    // POSTAL CODE
    // ========================================================

    if (!iso2) {
      setPostalDisabled(true);
      setPostalHint("Please select country / region first");
    } else if (iso2 === "US") {
      setPostalDisabled(false);
      setPostalHint("Required for United States");
    } else if (iso2 === "CA") {
      setPostalDisabled(false);
      setPostalHint("Required for Canada");
    } else {
      setPostalDisabled(true);
      setPostalHint("Not required");
    }

    // ========================================================
    // AUTO SELECT POSTAL CODE FROM GEOLOCATION
    // ========================================================

    if (
      isGeoMatch &&
      (iso2 === "US" || iso2 === "CA") &&
      String(geolocation?.postal_code || "").trim()
    ) {
      setPostalCode(String(geolocation.postal_code).trim().toUpperCase());
    }

    // ========================================================
    // STATE
    // ========================================================

    if (!iso2) {
      setStateDisabled(true);
      setStateHint("Please select country / region first");

      return;
    }

    if (iso2 === "SG") {
      setStateDisabled(true);
      setStateHint("Not required");

      if (isGeoMatch) {
        geoAppliedRef.current = true;
      }

      return;
    }

    setStateDisabled(true);
    setStateHint("Loading states...");

    try {
      const results = State.getStatesOfCountry(iso2);

      const states = (results || []).map((state, index) => ({
        index,

        value: state.isoCode || `${state.name}-${index}`,

        label: state.name,

        name: state.name,

        iso2: state.isoCode,

        country_code: state.countryCode,

        _raw: state,
      }));

      setStateOptions(states);

      if (!states.length) {
        setStateDisabled(true);
        setStateHint("Not required");

        if (isGeoMatch) {
          geoAppliedRef.current = true;
        }

        return;
      }

      setStateDisabled(false);
      setStateHint("Select state / territory");

      // ======================================================
      // AUTO SELECT STATE FROM GEOLOCATION
      // ======================================================

      if (isGeoMatch) {
        const geoStateName = String(geolocation?.state_name || "")
          .trim()
          .toLowerCase();

        if (geoStateName) {
          const matchedState = states.find(
            (state) =>
              String(state.name || "")
                .trim()
                .toLowerCase() === geoStateName,
          );

          if (matchedState) {
            setSelectedState(matchedState);
          }
        }

        geoAppliedRef.current = true;
      }
    } catch (error) {
      console.error("GetState failed:", error);

      setStateOptions([]);
      setStateDisabled(true);
      setStateHint("Not required");

      if (isGeoMatch) {
        geoAppliedRef.current = true;
      }
    }
  }, [selectedCountry, geolocation]);

  // ============================================================
  // NOTIFY PARENT WHEN LOCATION CHANGES
  // ============================================================

  useEffect(() => {
    if (typeof onLocationChange !== "function") {
      return;
    }

    const location = {
      country_iso2: getIso2(selectedCountry),

      state: String(selectedState?.name || "").trim(),

      postal_code: String(postalCode || "")
        .trim()
        .toUpperCase(),
    };

    onLocationChange(location, {
      isUserInitiated: userTouchedLocationRef.current,
    });
  }, [selectedCountry, selectedState, postalCode, onLocationChange]);

  // ============================================================
  // PAYMENT GATEWAYS
  // ============================================================

  const gateways = Array.isArray(pricePlan?.payment_gateway_options)
    ? pricePlan.payment_gateway_options
    : [];

  const hasGateways = gateways.length > 0;

  const [selectedGatewayId, setSelectedGatewayId] = useState(null);

  useEffect(() => {
    if (!hasGateways) {
      setSelectedGatewayId(null);
      return;
    }

    setSelectedGatewayId((previousId) => {
      const exists = gateways.some((gateway) => gateway?._id === previousId);

      return exists ? previousId : gateways[0]?._id || null;
    });
  }, [pricePlan?.payment_gateway_options]);

  // ============================================================
  // CHECK SELECTED GATEWAY AVAILABILITY
  // ============================================================

  const selectedGateway =
    gateways.find((gateway) => gateway?._id === selectedGatewayId) ||
    gateways[0] ||
    null;

  const gatewayVendor = String(selectedGateway?.vendor || "").toLowerCase();

  const hasAvailableGateway =
    Boolean(selectedGateway) && isVendorSupported(gatewayVendor);

  useEffect(() => {
    if (!gateways.length) {
      setSelectedGatewayId(null);
      return;
    }

    setSelectedGatewayId((previousId) => {
      const exists = gateways.some((gateway) => gateway?._id === previousId);

      return exists ? previousId : gateways[0]?._id || null;
    });
  }, [pricePlan?.payment_gateway_options]);

  // ============================================================
  // PAYMENT PAYLOAD
  // ============================================================

  const paymentPayload = {
    store_slug,
    price_slug,

    // Gateway selection
    gateways,
    selectedGatewayId,
    setSelectedGatewayId,

    // Stripe configuration
    stripeOptions: pricePlan?.stripe_options || {},

    freeTrialNoCard,

    // Customer information
    fullName: `${firstName.trim()} ${surname.trim()}`.trim(),
    firstName: firstName.trim(),
    lastName: surname.trim(),
    email: normalizeEmail(email),

    selectedCountry,
    selectedState,
    stateOptions,
    postalCode,
    dialingCode,
    contactNumber,

    // Agreement
    agreementAccepted,

    // Coupon
    couponCode,

    // Payment submission
    paymentHandlerRef,
    setIsProcessing,
    setSuccessCountdown,

    // Everflow
    ef_transaction_id,
    ef_oid,
    ef_affid,
  };

  // ============================================================
  // HANDLE CHECKOUT SUBMISSION
  // ============================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      processingRef.current ||
      isRecalculating ||
      Boolean(recalculationError) ||
      typeof successCountdown === "number"
    ) {
      return;
    }

    setPaymentErrors([]);

    const errors = validateCheckout();

    if (errors.length > 0) {
      setPaymentErrors(errors);
      return;
    }

    if (typeof paymentHandlerRef.current !== "function") {
      setPaymentErrors(["Payment method is not available. Please try again."]);

      return;
    }

    processingRef.current = true;

    try {
      await paymentHandlerRef.current();
    } catch (error) {
      setPaymentErrors([error?.message || "Unable to process payment."]);
    } finally {
      processingRef.current = false;
    }
  };

  // ============================================================
  // RECURRING PRICE PLANS
  // ============================================================

  const recurringPlans = [
    ...(pricePlan?.recurring ? [pricePlan] : []),

    ...(Array.isArray(pricePlan?.bundles)
      ? pricePlan.bundles.filter((item) => item?.recurring)
      : []),
  ];

  const recurringPlanNames = recurringPlans
    .map((item) => item?.name)
    .filter(Boolean);

  const recurringPlanNamesText =
    recurringPlanNames.length > 1
      ? `${recurringPlanNames.slice(0, -1).join(", ")} & ${recurringPlanNames.at(-1)}`
      : recurringPlanNames[0] || "";

  const hasRecurringPlans = recurringPlanNames.length > 0;

  const hasMultipleRecurringPlans = recurringPlanNames.length > 1;

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <form
      className='checkout-card bg-white shadow-sm'
      onSubmit={handleSubmit}
      noValidate
    >
      {/* ================================================== */}
      {/* YOUR DETAILS */}
      {/* ================================================== */}

      <div className='checkout-form-section'>
        <p className='checkout-section-title'>
          <span className='checkout-section-title-dot' />
          Your details
        </p>

        {/* ================================================== */}
        {/* FIRST NAME / SURNAME */}
        {/* ================================================== */}

        <div className='checkout-row checkout-name-row'>
          {/* ================================================== */}
          {/* FIRST NAME */}
          {/* ================================================== */}

          <div className='checkout-field'>
            <label>First name</label>

            {firstNameTouched && firstNameError && (
              <div className='checkout-field-error'>{firstNameError}</div>
            )}

            <input
              type='text'
              className='checkout-input'
              value={firstName}
              placeholder='e.g. Harrison'
              autoComplete='given-name'
              onChange={(event) => {
                const value = event.target.value;

                setFirstName(value);

                if (firstNameTouched) {
                  setFirstNameError(validateFirstName(value));
                } else {
                  setFirstNameError("");
                }
              }}
              onBlur={(event) => {
                setFirstNameTouched(true);

                setFirstNameError(validateFirstName(event.target.value));
              }}
            />
          </div>

          {/* ================================================== */}
          {/* SURNAME */}
          {/* ================================================== */}

          <div className='checkout-field'>
            <label>Surname</label>

            {surnameTouched && surnameError && (
              <div className='checkout-field-error'>{surnameError}</div>
            )}

            <input
              type='text'
              className='checkout-input'
              value={surname}
              placeholder='e.g. Lee'
              autoComplete='family-name'
              onChange={(event) => {
                const value = event.target.value;

                setSurname(value);

                if (surnameTouched) {
                  setSurnameError(validateSurname(value));
                } else {
                  setSurnameError("");
                }
              }}
              onBlur={(event) => {
                setSurnameTouched(true);

                setSurnameError(validateSurname(event.target.value));
              }}
            />
          </div>
        </div>

        {/* ================================================== */}
        {/* EMAIL */}
        {/* ================================================== */}

        <div className='checkout-field'>
          <label>Email address</label>

          {emailTouched && emailError && (
            <div className='checkout-field-error'>{emailError}</div>
          )}

          <input
            type='email'
            className='checkout-input'
            value={email}
            placeholder='my-email@example.com'
            autoComplete='email'
            onChange={(event) => {
              const value = event.target.value;

              setEmail(value);

              const trimmed = String(value || "").trim();

              if (trimmed.length >= 5) {
                setEmailTouched(true);

                setEmailError(validateEmail(value));
              } else {
                setEmailError("");
              }
            }}
            onBlur={(event) => {
              setEmailTouched(true);

              setEmailError(validateEmail(event.target.value));
            }}
          />

          <div className='checkout-field-note'>
            Note: If you have an existing{" "}
            {pricePlan?.team?.name
              ? `${pricePlan.team.name} account`
              : "account with us"}
            , please enter the same email address.
          </div>
        </div>

        {/* ================================================== */}
        {/* BILLING & CONTACT DETAILS */}
        {/* ================================================== */}

        <div className={freeTrialNoCard ? "d-none" : ""}>
          {/* ================================================== */}
          {/* COUNTRY / REGION */}
          {/* ================================================== */}

          <div className='checkout-field'>
            <label>Country / region</label>

            <div className='checkout-select'>
              <Select
                value={selectedCountry}
                onChange={(option) => {
                  userTouchedLocationRef.current = true;

                  // Reset dependent location values.
                  setSelectedState(null);
                  setPostalCode("");

                  setSelectedCountry(option);
                }}
                options={countryOptions}
                placeholder='Select country / region'
                classNamePrefix='checkout-react-select'
                styles={selectStyles}
              />
            </div>
          </div>

          {/* ================================================== */}
          {/* STATE / TERRITORY */}
          {/* ================================================== */}

          <div className='checkout-field'>
            <label>State / territory</label>

            <div className='checkout-select'>
              <Select
                value={selectedState}
                onChange={(option) => {
                  userTouchedLocationRef.current = true;

                  setSelectedState(option);
                }}
                options={stateOptions}
                placeholder={stateHint}
                isDisabled={stateDisabled}
                classNamePrefix='checkout-react-select'
                styles={selectStyles}
              />
            </div>
          </div>

          {/* ================================================== */}
          {/* ZIP / POSTAL CODE */}
          {/* ================================================== */}

          <div className='checkout-field'>
            <label>ZIP / Postal Code</label>

            {postalTouched && postalError && (
              <div className='checkout-field-error'>{postalError}</div>
            )}

            <input
              type='text'
              className='checkout-input'
              value={postalCode}
              disabled={postalDisabled}
              placeholder={postalHint}
              autoComplete='postal-code'
              onChange={(event) => {
                userTouchedLocationRef.current = true;

                const value = String(event.target.value || "").toUpperCase();

                setPostalCode(value);

                if (postalTouched) {
                  setPostalError(validatePostal(value, selectedCountry));
                } else {
                  setPostalError("");
                }
              }}
              onBlur={(event) => {
                setPostalTouched(true);

                setPostalError(
                  validatePostal(event.target.value, selectedCountry),
                );
              }}
            />
          </div>

          {/* ================================================== */}
          {/* CONTACT NUMBER */}
          {/* ================================================== */}

          <div className='checkout-field'>
            <label>Contact number</label>

            {contactTouched && contactError && (
              <div className='checkout-field-error'>{contactError}</div>
            )}

            <div
              className={`checkout-phone-input ${
                contactDisabled ? "is-disabled" : ""
              }`}
            >
              <div className='checkout-phone-code'>{dialingCode || "—"}</div>

              <input
                type='tel'
                inputMode='numeric'
                pattern='[0-9]*'
                className='checkout-phone-number'
                value={contactNumber}
                disabled={contactDisabled}
                autoComplete='tel-national'
                placeholder={
                  contactDisabled
                    ? "Select country / region first"
                    : "Contact number"
                }
                onChange={(event) => {
                  const clean = digitsOnly(event.target.value).slice(
                    0,
                    MAX_CONTACT_LEN,
                  );

                  setContactNumber(clean);

                  if (contactTouched) {
                    setContactError(
                      validateContactNumber(clean, selectedCountry),
                    );
                  } else {
                    setContactError("");
                  }
                }}
                onBlur={() => {
                  setContactTouched(true);

                  setContactError(
                    validateContactNumber(contactNumber, selectedCountry),
                  );
                }}
                onPaste={(event) => {
                  event.preventDefault();

                  const pasted = digitsOnly(
                    event.clipboardData.getData("text"),
                  );

                  setContactNumber((previousValue) =>
                    `${previousValue}${pasted}`.slice(0, MAX_CONTACT_LEN),
                  );
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ================================================== */}
      {/* PAYMENT METHOD */}
      {/* ================================================== */}

      <hr />

      <PaymentSection payload={paymentPayload} />

      {/* ================================================== */}
      {/* AGREEMENT */}
      {/* ================================================== */}

      <div className='checkout-form-section mb-3'>
        <div className='checkout-agreement'>
          {legalContentsHtml && (
            <SanitizedHTML
              html={legalContentsHtml}
              className='checkout-legal-contents'
            />
          )}

          {/* <p className='checkout-field-note'>
            If you bought a subscription, we will automatically renew it and
            charge your payment method until you cancel.
          </p> */}

          {hasRecurringPlans && !freeTrialNoCard && (
            <p className='checkout-field-note fst-italic mt-3'>
              {recurringPlanNamesText}{" "}
              {hasMultipleRecurringPlans
                ? "are subscriptions"
                : "is a subscription"}
              .{" "}
              {pricePlan?.team?.company_name
                ? pricePlan?.team?.company_name
                : "We"}{" "}
              will automatically renew{" "}
              {hasMultipleRecurringPlans ? "them" : "it"} and charge your
              payment method until you cancel.
            </p>
          )}

          <label className='checkout-checkbox'>
            <input
              type='checkbox'
              checked={agreementAccepted}
              onChange={(event) => {
                const checked = event.target.checked;

                setAgreementAccepted(checked);

                if (agreementTouched && checked) {
                  setAgreementTouched(false);
                }
              }}
              onBlur={() => {
                setAgreementTouched(true);
              }}
            />

            <span>Yes, I&apos;ve read and agree to all of the above.</span>
          </label>

          {agreementTouched && !agreementAccepted && (
            <div className='checkout-field-error mt-2'>
              🚨 You must agree to the terms before completing your purchase.
            </div>
          )}
        </div>
      </div>

      {/* ================================================== */}
      {/* RECALCULATION ERROR */}
      {/* ================================================== */}

      {recalculationError && (
        <SanitizedHTML
          html={recalculationError}
          className='alert alert-danger mb-3'
        />
      )}

      {/* ================================================== */}
      {/* PAYMENT ERRORS */}
      {/* ================================================== */}

      {paymentErrors.length > 0 && (
        <div className='alert alert-danger mb-3 paymentErrors'>
          <ul className='mb-0 ps-3'>
            {paymentErrors.map((error, index) => (
              <li key={index}>
                <SanitizedHTML html={error} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ================================================== */}
      {/* CTA */}
      {/* ================================================== */}

      {hasAvailableGateway && (
        <button
          type='submit'
          className='checkout-cta'
          disabled={
            isProcessing ||
            isRecalculating ||
            Boolean(recalculationError) ||
            typeof successCountdown === "number"
          }
          style={{
            boxShadow: "rgba(0, 0, 0, 0.3) 0px 4px 8px",

            fontSize: `${ctaFontSize}px`,

            color: ctaColor,

            backgroundColor: ctaBackgroundColor,

            borderRadius: `${cta?.border_radius ?? 8}px`,

            opacity:
              isProcessing ||
              isRecalculating ||
              Boolean(recalculationError) ||
              typeof successCountdown === "number"
                ? 0.65
                : 1,

            cursor:
              isProcessing ||
              isRecalculating ||
              Boolean(recalculationError) ||
              typeof successCountdown === "number"
                ? "not-allowed"
                : "pointer",
          }}
        >
          {isProcessing
            ? "Processing..."
            : isRecalculating
              ? "Recalculating..."
              : ctaLabel}
        </button>
      )}

      {/* ================================================== */}
      {/* TRUST */}
      {/* ================================================== */}

      {hasAvailableGateway && (
        <div className='checkout-trust'>
          <span>🔒 Encrypted & Secure</span>
        </div>
      )}
    </form>
  );
};

export default PaymentForm;
